import { useState, useMemo } from 'react';
import { Search, ArrowUpDown, ChevronDown, User, Mail, Phone } from 'lucide-react';
import { parseDisplayName, formatDate } from '@/lib/utils';
import type { Person } from '@/types/leads';
import styles from './PersonsList.module.css';

interface PersonsListProps {
  persons: Person[];
  onSelect: (person: Person) => void;
}

type SortConfig = {
  key: 'fullName' | 'createdAt';
  direction: 'asc' | 'desc';
};

export const PersonsList = ({ persons, onSelect }: PersonsListProps) => {
  const [displayCount, setDisplayCount] = useState(5);
  const [searchQuery, setSearchQuery] = useState('');
  const [sort, setSort] = useState<SortConfig>({ key: 'createdAt', direction: 'desc' });

  const filteredAndSortedPersons = useMemo(() => {
    let result = [...persons];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((person) => {
        const name = (person.fullName || '').toLowerCase();
        const email = (person.email || '').toLowerCase();
        const phone = (person.phoneNumber || '').toLowerCase();
        return name.includes(query) || email.includes(query) || phone.includes(query);
      });
    }

    result.sort((a, b) => {
      if (sort.key === 'fullName') {
        const valA = a.fullName || '';
        const valB = b.fullName || '';
        return sort.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }

      const valA = new Date(a.createdAt).getTime();
      const valB = new Date(b.createdAt).getTime();
      return sort.direction === 'asc' ? valA - valB : valB - valA;
    });

    return result;
  }, [persons, searchQuery, sort]);

  const visiblePersons = filteredAndSortedPersons.slice(0, displayCount);
  const hasMore = displayCount < filteredAndSortedPersons.length;

  const handleSort = (key: SortConfig['key']) => {
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  return (
    <div className={styles.container}>
      <div className={styles.searchRow}>
        <div className={styles.searchWrapper}>
          <Search className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <button onClick={() => handleSort('fullName')} className={styles.sortButton}>
          <ArrowUpDown className={styles.sortButtonIcon} />
        </button>
      </div>

      <div className={styles.scrollArea}>
        <div className={styles.cardGrid}>
          {visiblePersons.map((person) => {
            const businessName = person.place
              ? parseDisplayName(person.place.displayName)?.text || 'Business'
              : null;

            return (
              <div key={person.id} onClick={() => onSelect(person)} className={styles.card}>
                <div className={styles.cardTop}>
                  <div className={styles.avatar}>
                    <User className={styles.avatarIcon} />
                  </div>
                  <div className={styles.cardInfo}>
                    <div className={styles.cardNameRow}>
                      <p className={styles.personName}>{person.fullName || 'No Name'}</p>
                      <span className={styles.personDate}>{formatDate(person.createdAt)}</span>
                    </div>
                    <div className={styles.contactDetails}>
                      {person.email && (
                        <span className={styles.contactItem}>
                          <Mail className={styles.contactItemIcon} />
                          {person.email}
                        </span>
                      )}
                      {person.phoneNumber && (
                        <span className={styles.contactItem}>
                          <Phone className={styles.contactItemIcon} />
                          {person.phoneNumber}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {person.place && (
                  <div className={styles.linkedSection}>
                    <span className={styles.linkedBadge}>Linked</span>
                    <span className={styles.linkedName}>{businessName}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {hasMore && (
          <button
            onClick={() => setDisplayCount((prev) => prev + 5)}
            className={styles.loadMoreButton}
          >
            <ChevronDown className={styles.loadMoreIcon} />
            Load more
          </button>
        )}

        {filteredAndSortedPersons.length === 0 && (
          <div className={styles.emptyState}>
            <p className={styles.emptyText}>No contacts match your search.</p>
          </div>
        )}
      </div>
    </div>
  );
};
